import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Video, ExternalLink, Clock, Users, CalendarDays, Loader2,
  CheckCircle, XCircle, Link2, Play, StopCircle
} from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format, isPast, isFuture, isToday } from "date-fns";
import { ar } from "date-fns/locale";
import AddLiveSessionDialog from "@/components/dialogs/AddLiveSessionDialog";
import { toast } from "@/components/ui/sonner";

const LiveSessions = () => {
  const { profile, user, roles } = useAuth();
  const nurseryId = profile?.nursery_id;
  const queryClient = useQueryClient();
  const isStaff = roles.includes("admin") || roles.includes("teacher");

  const { data: classes } = useQuery({
    queryKey: ["session-classes", nurseryId],
    queryFn: async () => {
      const { data } = await supabase.from("classes").select("id, name").eq("nursery_id", nurseryId!);
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: lessons } = useQuery({
    queryKey: ["session-lessons", nurseryId],
    queryFn: async () => {
      const { data } = await supabase.from("lessons").select("id, title, class_id").eq("nursery_id", nurseryId!);
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: childrenList } = useQuery({
    queryKey: ["session-children", nurseryId],
    queryFn: async () => {
      const { data } = await supabase.from("children").select("id, full_name, class_id").eq("nursery_id", nurseryId!);
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: sessions, isLoading } = useQuery({
    queryKey: ["live-sessions", nurseryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("live_sessions")
        .select("*, classes(name), lessons(title)")
        .eq("nursery_id", nurseryId!)
        .order("scheduled_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: attendanceCounts } = useQuery({
    queryKey: ["session-attendance-counts", nurseryId],
    queryFn: async () => {
      if (!sessions || sessions.length === 0) return {};
      const sessionIds = sessions.map((s: any) => s.id);
      const { data } = await supabase
        .from("session_attendance")
        .select("session_id")
        .in("session_id", sessionIds);
      const counts: Record<string, number> = {};
      (data || []).forEach((a: any) => {
        counts[a.session_id] = (counts[a.session_id] || 0) + 1;
      });
      return counts;
    },
    enabled: !!sessions && sessions.length > 0,
  });

  const updateStatus = async (sessionId: string, status: string) => {
    await supabase.from("live_sessions").update({ status } as any).eq("id", sessionId);
    queryClient.invalidateQueries({ queryKey: ["live-sessions"] });
    toast.success(status === "live" ? "تم بدء البث" : status === "ended" ? "تم إنهاء الجلسة" : "تم التحديث");
  };

  const upcoming = (sessions || []).filter((s: any) => s.status === "scheduled" && isFuture(new Date(s.scheduled_at)));
  const live = (sessions || []).filter((s: any) => s.status === "live");
  const today = (sessions || []).filter((s: any) => s.status === "scheduled" && isToday(new Date(s.scheduled_at)));
  const past = (sessions || []).filter((s: any) => s.status === "ended" || (s.status === "scheduled" && isPast(new Date(s.scheduled_at))));

  const platformLabel = (p: string) => {
    switch (p) {
      case "zoom": return "Zoom";
      case "google_meet": return "Google Meet";
      case "teams": return "Teams";
      default: return p;
    }
  };

  const platformColor = (p: string) => {
    switch (p) {
      case "zoom": return "bg-blue-500/10 text-blue-600";
      case "google_meet": return "bg-green-500/10 text-green-600";
      case "teams": return "bg-purple-500/10 text-purple-600";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const SessionCard = ({ session, index }: { session: any; index: number }) => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
    >
      <Card className={`shadow-card transition-all hover:shadow-elevated ${session.status === "live" ? "ring-2 ring-destructive animate-pulse" : ""}`}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${session.status === "live" ? "bg-destructive/10" : "bg-primary/10"}`}>
                <Video className={`w-5 h-5 ${session.status === "live" ? "text-destructive" : "text-primary"}`} />
              </div>
              <div>
                <h3 className="font-bold text-card-foreground">{session.title}</h3>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {session.classes?.name && (
                    <Badge variant="outline" className="text-xs">{session.classes.name}</Badge>
                  )}
                  <Badge className={`text-xs ${platformColor(session.meeting_platform)}`}>
                    {platformLabel(session.meeting_platform)}
                  </Badge>
                  {session.status === "live" && (
                    <Badge className="text-xs bg-destructive text-destructive-foreground animate-pulse">
                      <StopCircle className="w-3 h-3 ml-1" /> مباشر الآن
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="text-sm text-muted-foreground space-y-1.5 mb-4">
            <div className="flex items-center gap-2">
              <CalendarDays className="w-3.5 h-3.5" />
              {format(new Date(session.scheduled_at), "EEEE d MMMM yyyy", { locale: ar })}
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-3.5 h-3.5" />
              {format(new Date(session.scheduled_at), "hh:mm a", { locale: ar })}
              {session.duration_minutes && <span className="text-xs">({session.duration_minutes} دقيقة)</span>}
            </div>
            {attendanceCounts && attendanceCounts[session.id] > 0 && (
              <div className="flex items-center gap-2">
                <Users className="w-3.5 h-3.5" />
                {attendanceCounts[session.id]} حضروا
              </div>
            )}
            {session.lessons?.title && (
              <div className="flex items-center gap-2">
                <Link2 className="w-3.5 h-3.5" />
                مرتبط بدرس: {session.lessons.title}
              </div>
            )}
            {session.description && (
              <p className="text-xs mt-1">{session.description}</p>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              variant={session.status === "live" ? "default" : "outline"}
              className="gap-1.5"
              onClick={() => window.open(session.meeting_url, "_blank")}
            >
              <ExternalLink className="w-3.5 h-3.5" />
              {session.status === "live" ? "انضمام الآن" : "فتح الرابط"}
            </Button>

            {isStaff && session.status === "scheduled" && (
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5 text-primary"
                onClick={() => updateStatus(session.id, "live")}
              >
                <Play className="w-3.5 h-3.5" />
                بدء البث
              </Button>
            )}

            {isStaff && session.status === "live" && (
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5 text-destructive"
                onClick={() => updateStatus(session.id, "ended")}
              >
                <StopCircle className="w-3.5 h-3.5" />
                إنهاء الجلسة
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">البث المباشر</h1>
          <p className="text-muted-foreground text-sm mt-1">إنشاء وإدارة جلسات البث المباشر مع الطلبة</p>
        </div>
        {isStaff && nurseryId && classes && lessons && (
          <AddLiveSessionDialog
            classes={classes}
            lessons={lessons}
            children={childrenList || []}
            nurseryId={nurseryId}
            onSuccess={() => queryClient.invalidateQueries({ queryKey: ["live-sessions"] })}
          />
        )}
      </div>

      {/* Live Now Banner */}
      {live.length > 0 && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-3 h-3 rounded-full bg-destructive animate-pulse" />
              <h3 className="font-bold text-destructive">جلسات مباشرة الآن ({live.length})</h3>
            </div>
            <div className="space-y-3">
              {live.map((s: any, i: number) => (
                <SessionCard key={s.id} session={s} index={i} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="upcoming" dir="rtl">
        <TabsList>
          <TabsTrigger value="upcoming">القادمة ({upcoming.length + today.length})</TabsTrigger>
          <TabsTrigger value="past">السابقة ({past.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="mt-4">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : [...today, ...upcoming].length === 0 ? (
            <div className="text-center py-16">
              <Video className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground">لا توجد جلسات مجدولة</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {[...today, ...upcoming].map((s: any, i: number) => (
                <SessionCard key={s.id} session={s} index={i} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="past" className="mt-4">
          {past.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-muted-foreground">لا توجد جلسات سابقة</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {past.map((s: any, i: number) => (
                <SessionCard key={s.id} session={s} index={i} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LiveSessions;
